import { useState } from 'react';

import type { GameApi } from '../state/useGame.ts';
import { TIER_COOL, TIER_NICE, tierName } from '../engine/rules.ts';
import { tilesRemaining } from '../engine/game.ts';
import { HUNT, LADDER } from '../engine/modes.ts';
import { achievementMultiplier, claimedBy } from '../engine/achievements.ts';
import { LadderPanel } from './LadderPanel.tsx';
import { HuntPanel } from './HuntPanel.tsx';
import { SolutionPanel } from './SolutionPanel.tsx';
import { copyFor } from './modeCopy.ts';

function tierClass(tier: number): string {
  if (tier === TIER_COOL) return 'history__word history__word--cool';
  if (tier === TIER_NICE) return 'history__word history__word--nice';

  return 'history__word';
}

export function PlayScreen({
  game,
  board,
}: {
  game: GameApi;
  board: React.ReactNode;
}): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState<'restart' | 'finish' | null>(null);

  const state = game.state!;
  const puzzle = game.puzzle!;
  const pricing = game.pricing;

  const canSubmit = !game.busy && pricing?.valid === true;
  const isLadder = game.mode.id === LADDER.id;
  const isHunt = game.mode.id === HUNT.id;
  const copy = copyFor(game.mode.id);

  // The selection, as a Move, so the achievements panel can ask the ENGINE whether it
  // would take one rather than re-implementing the rule.
  const liveMove =
    pricing?.valid === true
      ? {
          word: pricing.word,
          tiles: game.selection.map((tile) => ({ x: tile.x, y: tile.y })),
          score: pricing.score,
          tier: pricing.tier,
          claimed: [],
        }
      : null;

  // What the word would ACTUALLY pay. Showing the base score next to a panel promising
  // double points is a small lie, and the player finds out at exactly the moment the
  // number changes under them.
  const liveClaims =
    liveMove && puzzle.achievements?.length
      ? claimedBy(
          puzzle.achievements,
          new Set(state.moves.flatMap((m) => m.claimed)),
          liveMove,
        )
      : [];

  const liveScore = pricing ? pricing.score * achievementMultiplier(liveClaims.length) : 0;
  const remaining = tilesRemaining(state.board);

  let best = 0;
  let longest = 0;

  for (const move of state.moves) {
    if (move.score > best) best = move.score;
    if (move.word.length > longest) longest = move.word.length;
  }

  return (
    <div className="play">
      <header className="play__head">
        <div>
          <p className="wordmark wordmark--small">
            word soup<span className="dot">.</span>
          </p>
          <p className="play__puzzle">
            {copy.label} #{puzzle.displayNumber} · {remaining} tile
            {remaining === 1 ? '' : 's'} left
          </p>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div className="play__score-label">Score</div>
          <div className="play__score" aria-live="polite">
            {state.score}
          </div>
        </div>
      </header>

      <div className="play__main">
        <div className="play__board">
          {/* The live selection. aria-live so a screen reader hears the word build up. */}
          <div className="current" aria-live="polite">
            {pricing && pricing.word.length > 0 ? (
              <>
                <span
                  className={
                    pricing.valid ? 'current__word' : 'current__word current__word--invalid'
                  }
                >
                  {pricing.word}
                </span>
                {pricing.valid ? (
                  <>
                    <span className="current__score">+{liveScore}</span>
                {liveClaims.length > 0 ? (
                  <span className="current__tier current__tier--bonus">
                    ×{achievementMultiplier(liveClaims.length)}
                  </span>
                ) : null}
                    {tierName(pricing.tier) ? (
                      <span className="current__tier">{tierName(pricing.tier)}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="current__hint">{pricing.reason}</span>
                )}
              </>
            ) : (
              <span className="current__hint">{copy.selectHint}</span>
            )}
          </div>

          {board}

          <div className="play__actions">
            <button className="button" type="button" onClick={game.submit} disabled={!canSubmit}>
              Submit word
            </button>

            <div className="play__secondary">
              <button
                className="button button--ghost"
                type="button"
                onClick={game.backspace}
                disabled={game.busy || game.selection.length === 0}
              >
                ⌫ Backspace
              </button>

              <button
                className="button button--ghost"
                type="button"
                onClick={game.cancelSelection}
                disabled={game.busy || game.selection.length === 0}
              >
                Clear
              </button>

              <button
                className="button button--ghost"
                type="button"
                onClick={game.undo}
                disabled={game.busy || state.moves.length === 0}
              >
                ↺ Undo word
              </button>

              {game.mode.hasShuffle ? (
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={game.shuffle}
                  disabled={game.busy || game.shufflesLeft === 0}
                  title="Rearrange the letters still on the board"
                >
                  ⤫ Shuffle ({game.shufflesLeft})
                </button>
              ) : null}

              <button
                className="button button--ghost"
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-haspopup="dialog"
              >
                More
              </button>
            </div>
          </div>
        </div>

        {isHunt ? (
          <HuntPanel
            achievements={puzzle.achievements ?? []}
            moves={state.moves}
            live={liveMove}
          />
        ) : null}

        {/* The words the grid was built from. Ladder shows its own route inside the
            panel above, so this is for every other mode - and only when the site was
            configured to send it at all. */}
        {!isLadder && puzzle.solution ? (
          <SolutionPanel
            solution={puzzle.solution}
            moves={state.moves}
            achievements={puzzle.achievements ?? []}
          />
        ) : null}

        {isLadder ? (
          <LadderPanel
            moves={state.moves}
            solution={puzzle.solution}
            liveLength={pricing?.word.length ?? 0}
            liveValid={pricing?.valid === true}
          />
        ) : null}
      </div>

      {/* The mockup's "words to find" panel. Score Attack has no list to find, so it
          shows the words already made instead; Ladder genuinely has one, and shows it in
          LadderPanel above rather than duplicating it here. */}
      {isLadder ? null : (
      <section className="history">
        <div className="history__head">
          <h2 className="history__title">
            Your words
          </h2>
          <span className="history__summary">
            {state.moves.length === 0
              ? '—'
              : `best ${best} · longest ${longest} letter${longest === 1 ? '' : 's'}`}
          </span>
        </div>

        {state.moves.length === 0 ? (
          <p className="history__empty">Nothing yet. Three letters is the shortest word.</p>
        ) : (
          <ul className="history__list">
            {state.moves
              .map((move, index) => ({ move, index }))
              .reverse()
              .map(({ move, index }) => (
                <li className="history__item" key={`${move.word}-${index}`}>
                  <span className={tierClass(move.tier)}>{move.word}</span>
                  <span className="history__points">+{move.score}</span>
                </li>
              ))}
          </ul>
        )}
      </section>
      )}

      {menuOpen ? (
        <div
          className="sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Game options"
          onClick={() => setMenuOpen(false)}
        >
          <div className="sheet__panel" onClick={(event) => event.stopPropagation()}>
            <h2 className="sheet__title">Options</h2>
            <p className="sheet__body">
              Restarting gives you a fresh run at today’s puzzle. Your best score for today
              is kept either way.
            </p>

            <div className="entry__actions">
              <button
                className="button button--secondary"
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setConfirming('restart');
                }}
              >
                Restart
              </button>
              <button
                className="button button--secondary"
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setConfirming('finish');
                }}
              >
                Finish now
              </button>
              <button className="button" type="button" onClick={() => setMenuOpen(false)}>
                Keep playing
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirming ? (
        <div className="sheet" role="dialog" aria-modal="true" aria-label="Confirm">
          <div className="sheet__panel">
            <h2 className="sheet__title">
              {confirming === 'restart' ? 'Restart the puzzle?' : 'Finish now?'}
            </h2>
            <p className="sheet__body">
              {confirming === 'restart'
                ? 'This clears the words you have made so far and deals today’s board again from the start. Your best score for today is not affected.'
                : `You will finish with ${state.score} point${state.score === 1 ? '' : 's'} and ${remaining} tile${remaining === 1 ? '' : 's'} still on the board.`}
            </p>

            <div className="button-row">
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setConfirming(null)}
              >
                Cancel
              </button>
              <button
                className="button"
                type="button"
                onClick={() => {
                  const action = confirming;

                  setConfirming(null);

                  if (action === 'restart') game.restart();
                  else game.finishEarly();
                }}
              >
                {confirming === 'restart' ? 'Restart' : 'Finish'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
